import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
  editRoadmapPhase,
  generateAllCampaignPosts,
  generateCampaignPost,
  getCampaign,
} from '../api/client';
import type {
  CampaignDetail,
  CampaignGenerationSummary,
  CampaignRoadmapPhase,
  PhaseType,
} from '../api/types';
import { ApiClientError } from '../api/types';
import '../components/ui/ui-button';
import '../components/ui/ui-input';
import '../components/ui/ui-double-confirm';
import { alertStyles, motionStyles, boxSizingStyles, responsiveStyles } from '../styles/lit-shared';
import { navigate } from '../router';

const PHASE_LABELS: Record<PhaseType, string> = {
  statu_quo: 'Romper el Statu Quo',
  perspective_shift: 'Cambio de Perspectiva',
  objections: 'Autoridad y Objeciones',
  climax: 'El Clímax',
};

const GEN_STATUS_CONFIG: Record<string, { label: string; class: string }> = {
  pending: { label: 'Artículo en cola', class: 'gen-badge--pending' },
  processing: { label: 'Artículo Procesando', class: 'gen-badge--processing' },
  completed: { label: 'Artículo Listo', class: 'gen-badge--completed' },
  failed: { label: 'Requiere Reintento', class: 'gen-badge--failed' },
};

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

interface EditState {
  strategicAngle: string;
  narrativeHook: string;
  evolutionGoal: string;
  saving: boolean;
  error: string;
}

@customElement('campaign-detail-page')
export class CampaignDetailPage extends LitElement {
  @property({ type: String }) campaignId = '';

  @state() private detail: CampaignDetail | null = null;
  @state() private loading = true;
  @state() private error = '';

  @state() private editingPhase: number | null = null;
  @state() private editState: EditState = {
    strategicAngle: '',
    narrativeHook: '',
    evolutionGoal: '',
    saving: false,
    error: '',
  };

  @state() private generatingPhase: number | null = null;
  @state() private generatingAll = false;
  @state() private generateAllConfirmOpen = false;
  @state() private metaExpanded = true;

  private pollTimer: ReturnType<typeof setTimeout> | null = null;

  static styles = [
    boxSizingStyles,
    responsiveStyles,
    motionStyles,
    alertStyles,
    css`
      :host { display: block; }

      /* ─── Cabecera hero ─── */
      .hero {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: var(--space-md);
        margin-bottom: var(--space-xl);
        padding: var(--space-xl);
        border-radius: var(--radius-lg);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        overflow: hidden;
      }

      .hero::before {
        content: '';
        position: absolute;
        top: -40%;
        right: -5%;
        width: 220px;
        height: 220px;
        border-radius: 50%;
        background: var(--glow-violet);
        filter: blur(50px);
        pointer-events: none;
      }

      .hero-top {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-md);
        flex-wrap: wrap;
        width: 100%;
      }

      .hero-text {
        position: relative;
        z-index: 1;
        width: 100%;
        min-width: 0;
      }

      .hero-eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        padding: 0.25rem 0.75rem;
        border-radius: var(--radius-full);
        background: rgb(255 255 255 / 6%);
        border: 1px solid var(--color-border);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-accent);
        cursor: pointer;
        flex-shrink: 0;
      }

      .hero-eyebrow:hover { background: rgb(255 255 255 / 10%); }

      h1 {
        font-size: clamp(1.25rem, 3vw, 1.75rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin-bottom: var(--space-sm);
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        word-break: break-word;
      }

      .hero-meta {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        font-size: 0.875rem;
        color: var(--color-text-muted);
      }

      .hero-meta span strong { color: var(--color-text); font-weight: 600; }

      .hero-actions {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        flex-shrink: 0;
        flex-wrap: wrap;
        margin-left: auto;
      }

      /* ─── Status general ─── */
      .status-block {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-md);
        padding: var(--space-2xl);
        text-align: center;
        color: var(--color-text-muted);
      }

      .status-block p { font-size: 0.9375rem; }

      .error-block {
        padding: var(--space-lg);
        border-radius: var(--radius);
        background: rgb(244 63 94 / 8%);
        border: 1px solid rgb(251 113 133 / 25%);
        color: var(--color-error);
        font-size: 0.9375rem;
      }

      /* ─── Meta de campaña (colapsable) ─── */
      .meta-section {
        margin-bottom: var(--space-xl);
        border-radius: var(--radius-lg);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        overflow: hidden;
      }

      .meta-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-md) var(--space-lg);
        cursor: pointer;
        user-select: none;
        transition: background var(--duration-fast) var(--ease-out);
      }

      .meta-header:hover { background: var(--color-surface-hover); }

      .meta-header h2 {
        font-size: 0.9375rem;
        font-weight: 700;
        color: var(--color-text);
        letter-spacing: -0.01em;
      }

      .meta-chevron {
        font-size: 0.875rem;
        color: var(--color-text-muted);
        transition: transform var(--duration-fast) var(--ease-out);
      }

      .meta-chevron--open { transform: rotate(180deg); }

      .meta-body {
        padding: var(--space-lg);
        border-top: 1px solid var(--color-border);
        display: flex;
        flex-direction: column;
        gap: var(--space-md);
      }

      .meta-item {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .meta-label {
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-text-muted);
      }

      .meta-value {
        font-size: 0.9375rem;
        color: var(--color-text);
        line-height: 1.55;
      }

      .tone-chips {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-xs);
        margin-top: 0.25rem;
      }

      .tone-chip {
        padding: 0.25rem 0.625rem;
        border-radius: var(--radius-full);
        background: rgb(139 92 246 / 12%);
        border: 1px solid rgb(139 92 246 / 25%);
        font-size: 0.75rem;
        font-weight: 500;
        color: var(--color-primary-light);
      }

      /* ─── Roadmap ─── */
      .roadmap-header {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        margin-bottom: var(--space-lg);
      }

      .roadmap-header h2 {
        font-size: 1.125rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--color-text);
      }

      .roadmap-header::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, var(--color-border-strong) 0%, transparent 100%);
      }

      .roadmap {
        display: flex;
        flex-direction: column;
        gap: var(--space-md);
      }

      /* ─── Tarjeta de fase ─── */
      .phase-card {
        border-radius: var(--radius);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        overflow: hidden;
      }

      .phase-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-md);
        padding: var(--space-md) var(--space-lg);
        flex-wrap: wrap;
      }

      .phase-number-block {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--space-sm);
      }

      .phase-number {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        border-radius: var(--radius-sm);
        background: var(--gradient-primary);
        font-size: 0.75rem;
        font-weight: 700;
        color: white;
        flex-shrink: 0;
      }

      .phase-title {
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--color-text);
      }

      .phase-type-badge {
        padding: 0.2rem 0.6rem;
        border-radius: var(--radius-full);
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        background: rgb(139 92 246 / 12%);
        border: 1px solid rgb(139 92 246 / 25%);
        color: var(--color-primary-light);
      }

      .phase-actions {
        display: flex;
        align-items: center;
        gap: var(--space-xs);
        flex-wrap: wrap;
      }

      .phase-body {
        padding: 0 var(--space-lg) var(--space-lg);
        border-top: 1px solid var(--color-border);
      }

      .phase-fields {
        display: flex;
        flex-direction: column;
        gap: var(--space-sm);
        padding-top: var(--space-md);
      }

      .field-group {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .field-label {
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-text-muted);
      }

      .field-value {
        font-size: 0.9375rem;
        color: var(--color-text);
        line-height: 1.55;
      }

      /* ─── Edit form ─── */
      .edit-form {
        display: flex;
        flex-direction: column;
        gap: var(--space-md);
        padding-top: var(--space-md);
        border-top: 1px solid var(--color-border);
        margin-top: var(--space-md);
      }

      .edit-actions {
        display: flex;
        gap: var(--space-sm);
        justify-content: flex-end;
        flex-wrap: wrap;
      }

      /* ─── Generaciones vinculadas ─── */
      .gens-section {
        margin-top: var(--space-md);
        padding-top: var(--space-md);
        border-top: 1px solid var(--color-border);
      }

      .gens-label {
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-text-muted);
        margin-bottom: var(--space-sm);
      }

      .gen-chips {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-xs);
      }

      .gen-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.25rem 0.625rem;
        border-radius: var(--radius-full);
        font-size: 0.75rem;
        font-weight: 600;
        cursor: pointer;
        border: 1px solid transparent;
        text-decoration: none;
        transition:
          opacity var(--duration-fast) var(--ease-out),
          transform var(--duration-fast) var(--ease-out);
      }

      .gen-chip:hover { opacity: 0.85; transform: scale(1.02); }

      .gen-badge--pending {
        background: rgb(251 191 36 / 12%);
        color: var(--color-warning);
        border-color: rgb(251 191 36 / 25%);
      }

      .gen-badge--processing {
        background: rgb(139 92 246 / 12%);
        color: var(--color-primary-light);
        border-color: rgb(139 92 246 / 25%);
      }

      .gen-badge--completed {
        background: rgb(52 211 153 / 12%);
        color: var(--color-success);
        border-color: rgb(52 211 153 / 25%);
      }

      .gen-badge--failed {
        background: rgb(251 113 133 / 12%);
        color: var(--color-error);
        border-color: rgb(251 113 133 / 25%);
      }

      .gen-badge--refunded {
        background: rgb(251 191 36 / 12%);
        color: var(--color-warning);
        border-color: rgb(251 191 36 / 25%);
        cursor: default;
        font-size: 12px;
      }

      .gen-badge--refunded:hover {
        opacity: 1;
        transform: none;
      }

      .gen-dot {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: currentColor;
        flex-shrink: 0;
      }

      .gen-chip-icon {
        font-size: 0.7rem;
        flex-shrink: 0;
        line-height: 1;
      }

      /* ─── States ─── */
      .loading-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--space-md);
        padding: var(--space-2xl) 0;
        text-align: center;
        color: var(--color-text-muted);
      }

      @media (max-width: 639px) {
        .hero {
          padding: var(--space-lg);
        }

        .hero-top {
          align-items: flex-start;
        }

        .hero-actions {
          margin-left: 0;
        }
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopPoll();
  }

  private stopPoll() {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private schedulePoll() {
    this.stopPoll();
    this.pollTimer = setTimeout(() => { void this.loadSilent(); }, 5000);
  }

  private shouldPoll(): boolean {
    if (!this.detail) return false;
    if (this.detail.status === 'planning') return true;
    return this.detail.generations.some(
      (g) => g.status === 'pending' || g.status === 'processing',
    );
  }

  private phaseHasActiveGeneration(phase: number): boolean {
    return this.detail?.generations.some(
      (g) =>
        g.campaignPhase === phase
        && (g.status === 'pending' || g.status === 'processing'),
    ) ?? false;
  }

  private getPendingPhasesCount(): number {
    if (!this.detail?.masterPlan) return 0;
    return this.detail.masterPlan.roadmap.filter(
      (phase) =>
        (!phase.generations || phase.generations.length === 0)
        && !this.phaseHasActiveGeneration(phase.phase),
    ).length;
  }

  private async load() {
    this.loading = true;
    this.error = '';
    try {
      this.detail = await getCampaign(this.campaignId);
      if (this.shouldPoll()) this.schedulePoll();
    } catch (err) {
      this.error =
        err instanceof ApiClientError
          ? err.message
          : 'Error al cargar la campaña.';
    } finally {
      this.loading = false;
    }
  }

  private async loadSilent() {
    try {
      this.detail = await getCampaign(this.campaignId);
      if (this.shouldPoll()) this.schedulePoll();
    } catch {
      // silencioso — ya hay error en pantalla si falla load inicial
    }
  }

  // ──────────────────────────────────────────────────
  // Edición de fase
  // ──────────────────────────────────────────────────

  private startEdit(phase: CampaignRoadmapPhase) {
    this.editingPhase = phase.phase;
    this.editState = {
      strategicAngle: phase.strategic_angle,
      narrativeHook: phase.narrative_hook,
      evolutionGoal: phase.evolution_goal,
      saving: false,
      error: '',
    };
  }

  private cancelEdit() {
    this.editingPhase = null;
  }

  private async saveEdit() {
    if (!this.detail || this.editingPhase === null) return;

    this.editState = { ...this.editState, saving: true, error: '' };

    try {
      await editRoadmapPhase(this.campaignId, this.editingPhase, {
        strategicAngle: this.editState.strategicAngle,
        narrativeHook: this.editState.narrativeHook,
        evolutionGoal: this.editState.evolutionGoal,
      });

      // Actualizar localmente sin re-fetch
      const roadmap = this.detail.masterPlan!.roadmap.map((p) => {
        if (p.phase !== this.editingPhase) return p;
        return {
          ...p,
          strategic_angle: this.editState.strategicAngle,
          narrative_hook: this.editState.narrativeHook,
          evolution_goal: this.editState.evolutionGoal,
        };
      });

      this.detail = {
        ...this.detail,
        masterPlan: { ...this.detail.masterPlan!, roadmap },
      };

      this.editingPhase = null;
    } catch (err) {
      this.editState = {
        ...this.editState,
        saving: false,
        error:
          err instanceof ApiClientError
            ? err.message
            : 'Error al guardar los cambios.',
      };
    }
  }

  // ──────────────────────────────────────────────────
  // Generación
  // ──────────────────────────────────────────────────

  private openGenerateAllConfirm() {
    if (this.getPendingPhasesCount() === 0) return;
    this.generateAllConfirmOpen = true;
  }

  private closeGenerateAllConfirm() {
    if (this.generatingAll) return;
    this.generateAllConfirmOpen = false;
  }

  private async handleGenerateAllConfirm() {
    this.generateAllConfirmOpen = false;
    await this.generateAll();
  }

  private async generatePost(phase: number) {
    if (this.phaseHasActiveGeneration(phase)) return;

    this.generatingPhase = phase;
    try {
      const result = await generateCampaignPost(this.campaignId, phase);
      await this.loadSilent();
      void navigate(`/content/${result.generationId}`);
    } catch (err) {
      const msg =
        err instanceof ApiClientError ? err.message : 'Error al generar el post.';
      alert(msg);
    } finally {
      this.generatingPhase = null;
    }
  }

  private async generateAll() {
    this.generatingAll = true;
    try {
      await generateAllCampaignPosts(this.campaignId);
      await this.load();
    } catch (err) {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : 'Error al generar todos los posts.';
      alert(msg);
    } finally {
      this.generatingAll = false;
    }
  }

  // ──────────────────────────────────────────────────
  // Render helpers
  // ──────────────────────────────────────────────────

  private renderGenChip(gen: CampaignGenerationSummary) {
    const cfg = GEN_STATUS_CONFIG[gen.status] ?? GEN_STATUS_CONFIG.pending;
    const label = gen.title ?? `Post #${gen.campaignPhase ?? '?'}`;

    if (gen.status === 'completed') {
      return html`
        <span
          class="gen-chip ${cfg.class}"
          role="link"
          tabindex="0"
          title="Ver artículo generado"
          @click=${() => void navigate(`/content/${gen.id}`)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter') void navigate(`/content/${gen.id}`);
          }}
        >
          <span class="gen-chip-icon" aria-hidden="true">↗</span>
          Ver artículo
        </span>
      `;
    }

    const isProcessing = gen.status === 'processing';

    return html`
      <span class="gen-chip ${cfg.class}" title=${gen.errorMessage ?? label}>
        ${isProcessing
          ? html`<div class="spinner" style="width:10px;height:10px;border-width:1.5px;"></div>`
          : html`<span class="gen-dot"></span>`}
        ${gen.errorMessage && isProcessing ? gen.errorMessage : cfg.label}
      </span>
      ${gen.status === 'failed' ? html`
        <span class="gen-chip gen-badge--refunded">
          El coste de la generación ha sido reembolsado.
        </span>
      ` : nothing}
    `;
  }

  private renderPhaseCard(phase: CampaignRoadmapPhase, gens: CampaignGenerationSummary[]) {
    const isEditing = this.editingPhase === phase.phase;
    const isGenerating = this.generatingPhase === phase.phase;
    const phaseLabel = PHASE_LABELS[phase.phase_type] ?? phase.phase_type;
    const generateBlocked =
      this.phaseHasActiveGeneration(phase.phase) || isEditing || isGenerating;
    const editBlocked = (this.editingPhase !== null && !isEditing) || isGenerating;

    return html`
      <div class="phase-card">
        <div class="phase-card-header">
          <div class="phase-number-block">
            <span class="phase-number">${phase.phase}</span>
            <span class="phase-title">Post ${phase.phase}</span>
            <span class="phase-type-badge">${phaseLabel}</span>
          </div>
          <div class="phase-actions">
            <ui-button
              variant="secondary"
              size="sm"
              ?disabled=${editBlocked || isGenerating}
              @ui-click=${() => this.startEdit(phase)}
            >✏ Editar</ui-button>
            <ui-button
              size="sm"
              ?loading=${isGenerating}
              ?disabled=${generateBlocked || this.generatingAll}
              title=${generateBlocked
                ? isEditing
                  ? 'Guarda o cancela la edición antes de generar'
                  : this.phaseHasActiveGeneration(phase.phase)
                    ? 'Este post ya está en cola o procesándose'
                    : 'Generación no disponible'
                : 'Generar post de esta fase'}
              @ui-click=${() => void this.generatePost(phase.phase)}
            >▶ Generar Post</ui-button>
          </div>
        </div>

        <div class="phase-body">
          ${!isEditing
            ? html`
              <div class="phase-fields">
                <div class="field-group">
                  <span class="field-label">Ángulo estratégico</span>
                  <p class="field-value">${phase.strategic_angle}</p>
                </div>
                <div class="field-group">
                  <span class="field-label">Hook narrativo</span>
                  <p class="field-value">${phase.narrative_hook}</p>
                </div>
                <div class="field-group">
                  <span class="field-label">Objetivo evolutivo</span>
                  <p class="field-value">${phase.evolution_goal}</p>
                </div>
              </div>
            `
            : html`
              <div class="edit-form">
                <ui-input
                  label="Ángulo estratégico"
                  .value=${this.editState.strategicAngle}
                  .maxlength=${500}
                  @value-changed=${(e: CustomEvent<{ value: string }>) => {
                    this.editState = { ...this.editState, strategicAngle: e.detail.value };
                  }}
                ></ui-input>
                <ui-input
                  label="Hook narrativo"
                  .value=${this.editState.narrativeHook}
                  .maxlength=${500}
                  @value-changed=${(e: CustomEvent<{ value: string }>) => {
                    this.editState = { ...this.editState, narrativeHook: e.detail.value };
                  }}
                ></ui-input>
                <ui-input
                  label="Objetivo evolutivo"
                  .value=${this.editState.evolutionGoal}
                  .maxlength=${500}
                  @value-changed=${(e: CustomEvent<{ value: string }>) => {
                    this.editState = { ...this.editState, evolutionGoal: e.detail.value };
                  }}
                ></ui-input>
                ${this.editState.error
                  ? html`<div class="alert alert--error">${this.editState.error}</div>`
                  : nothing}
                <div class="edit-actions">
                  <ui-button
                    variant="secondary"
                    size="sm"
                    ?disabled=${this.editState.saving}
                    @ui-click=${this.cancelEdit}
                  >Cancelar</ui-button>
                  <ui-button
                    size="sm"
                    ?loading=${this.editState.saving}
                    ?disabled=${this.editState.saving}
                    @ui-click=${() => void this.saveEdit()}
                  >Guardar</ui-button>
                </div>
              </div>
            `}

          ${gens.length > 0
            ? html`
              <div class="gens-section">
                <p class="gens-label">Generaciones vinculadas</p>
                <div class="gen-chips">
                  ${gens.map((g) => this.renderGenChip(g))}
                </div>
              </div>
            `
            : nothing}
        </div>
      </div>
    `;
  }

  private renderMeta() {
    if (!this.detail?.masterPlan) return nothing;
    const meta = this.detail.masterPlan.campaign_meta;

    return html`
      <div class="meta-section">
        <div
          class="meta-header"
          @click=${() => { this.metaExpanded = !this.metaExpanded; }}
        >
          <h2>◈ Estrategia global de la campaña</h2>
          <span class="meta-chevron ${this.metaExpanded ? 'meta-chevron--open' : ''}">▾</span>
        </div>
        ${this.metaExpanded
          ? html`
            <div class="meta-body">
              <div class="meta-item">
                <span class="meta-label">Objetivo global</span>
                <p class="meta-value">${meta.global_objective}</p>
              </div>
              <div class="meta-item">
                <span class="meta-label">Psicología del target</span>
                <p class="meta-value">${meta.target_psychology}</p>
              </div>
              <div class="meta-item">
                <span class="meta-label">Reglas de tono canalla</span>
                <div class="tone-chips">
                  ${meta.rogue_tone_rules.map(
                    (rule) => html`<span class="tone-chip">${rule}</span>`,
                  )}
                </div>
              </div>
            </div>
          `
          : nothing}
      </div>
    `;
  }

  render() {
    if (this.loading && !this.detail) {
      return html`
        <div class="loading-state">
          <div class="spinner" role="status" aria-label="Cargando"></div>
          <p>Cargando campaña…</p>
        </div>
      `;
    }

    if (this.error) {
      return html`<div class="error-block">${this.error}</div>`;
    }

    if (!this.detail) return nothing;

    const { detail } = this;
    const pendingPhasesCount = this.getPendingPhasesCount();

    return html`
      <div class="animate-fade-up">

        <!-- Cabecera -->
        <section class="hero">
          <div class="hero-top">
            <span
              class="hero-eyebrow"
              role="link"
              tabindex="0"
              @click=${() => void navigate('/campaigns')}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') void navigate('/campaigns');
              }}
            >← Campañas</span>
            ${detail.status === 'active'
              ? html`
                <div class="hero-actions">
                  <ui-button
                    variant="secondary"
                    ?loading=${this.generatingAll}
                    ?disabled=${this.generatingAll || pendingPhasesCount === 0}
                    @ui-click=${this.openGenerateAllConfirm}
                  >
                    ${this.generatingAll ? 'Generando todos…' : '▶▶ Generar Todos'}
                  </ui-button>
                </div>
              `
              : nothing}
          </div>
          <div class="hero-text">
            <h1 title=${detail.topic}>${detail.topic}</h1>
            <div class="hero-meta">
              <span><strong>Topic:</strong> ${detail.topic}</span>
              <span><strong>Target:</strong> ${detail.targetAudience}</span>
              <span><strong>Conversión:</strong> ${detail.finalObjective}</span>
              <span>${formatDate(detail.createdAt)}</span>
            </div>
          </div>
        </section>

        <!-- Estado: planning -->
        ${detail.status === 'planning'
          ? html`
            <div class="status-block">
              <div class="spinner" role="status" aria-label="Procesando"></div>
              <p>El Director Estratégico está diseñando tu campaña…</p>
              <p style="font-size:0.8125rem; opacity:0.6;">Este proceso puede tardar hasta un minuto.</p>
            </div>
          `
          : nothing}

        <!-- Estado: failed -->
        ${detail.status === 'failed'
          ? html`
            <div class="error-block">
              <strong>Error al generar el plan maestro:</strong>
              ${detail.errorMessage ?? 'Error desconocido.'}
            </div>
          `
          : nothing}

        <!-- Estado: active -->
        ${detail.status === 'active' && detail.masterPlan
          ? html`
            ${this.renderMeta()}

            <div class="roadmap-header">
              <h2>Mapa de Ruta — 10 Posts</h2>
            </div>

            <div class="roadmap">
              ${detail.masterPlan.roadmap.map((phase) => {
                const gens = detail.generations.filter(
                  (g) => g.campaignPhase === phase.phase,
                );
                return this.renderPhaseCard(phase, gens);
              })}
            </div>
          `
          : nothing}

      </div>

      <ui-double-confirm
        .open=${this.generateAllConfirmOpen}
        title="Generar todos los posts"
        step1-message=${`¿Generar todos los posts pendientes? Se consumirán ${pendingPhasesCount} crédito${pendingPhasesCount === 1 ? '' : 's'}.`}
        step2-message=${`Confirma que quieres iniciar la generación de ${pendingPhasesCount} post${pendingPhasesCount === 1 ? '' : 's'}. Esta acción consumirá créditos de forma inmediata.`}
        confirm-label="Generar todos"
        .destructive=${false}
        .loading=${this.generatingAll}
        @modal-close=${this.closeGenerateAllConfirm}
        @confirm=${() => void this.handleGenerateAllConfirm()}
      ></ui-double-confirm>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'campaign-detail-page': CampaignDetailPage;
  }
}
